import { IconButton } from "@components/button";
import Clickable from "@components/clickable";
import FileUploadButton from "@components/fileUploadButton/FileUploadButton";
import FileDropArea from "@components/fileUploader/FileDropArea";
import FloatingPopper from "@components/floatingPopper/FloatingPopper";
import { CloseIcon } from "@components/icon";
import {
    ErrorWrapper,
    FileInputStyledMenu,
    FileNamesWrapper,
    StyledFileInput
} from "@components/inputs/fileInput/FileInput.styles";
import { WithErrorAndTooltip, withErrorAndTooltip } from "@components/inputs/formError/WithErrorAndTooltip";
import { ISharedInputProps } from "@components/inputs/input";
import { IconDelimiter, IconWrapper } from "@components/inputs/input/Input.styles";
import { POPOVER_INPUT_SHARP_LEFT_OFFSET_X } from "@components/popover/Popover.utils";
import Token, { ITokenProps } from "@components/token";
import Tokenizer from "@components/token/Tokenizer";
import { offset, shift } from "@floating-ui/react-dom";
import { handleRefHandlers, isDefined } from "@utils/general";
import { getZoomLevel } from "@utils/zoom.utils";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { FILES_API_URL } from "../../../constants";
import { BasicInputSizes, IconSize } from "../../../enums";
import TestIds from "../../../testIds";

export interface IFileTokenProps {
    id: string;
    name: string;
    link?: string;
    file?: File;
}

interface IProps extends ISharedInputProps {
    passRef?: React.Ref<HTMLDivElement>;
    width: string;
    files: IFileTokenProps[];
    multiple?: boolean;
    isLocalDropArea?: boolean;
    onNewFiles: (files: File[]) => void;
    onRemove: (id: string, e: React.MouseEvent) => void;
    onClick?: (id: string) => void;
}

interface IState {
    fileLinks: string[];
    isMenuOpen: boolean;
}

class FileInput extends React.PureComponent<IProps & WithErrorAndTooltip & WithTranslation, IState> {
    _refOpener = React.createRef<HTMLInputElement>();

    state: IState = {
        fileLinks: null,
        isMenuOpen: false,
    };

    componentWillUnmount() {
        this.revokeLinks();
    }

    get hasFiles(): boolean {
        return !!(this.props.files && this.props.files.length);
    }

    get maxTokenWidth(): string {
        let width = parseInt(this.props.width ?? BasicInputSizes.M);
        if (isNaN(width)) {
            width = 180;
        }
        width -= 55; // for upload button and paddings
        if (this.props.files && this.props.files.length > 1) {
            width -= 30; // for more button (e.g. +1)
        }
        return `${width}px`;
    }

    get tokens(): ITokenProps[] {
        return this.props.files?.map((file) => {
            const icon = this.props.isReadOnly ? {} : {
                icon: CloseIcon,
                onIconClick: this.handleRemove
            };

            return {
                id: file.id.toString(),
                title: file.name,
                color: "C_BG_menu",
                ...icon
            };
        }) ?? [];
    }

    // createLinks = (files: IFileTokenProps[]): void => {
    //     const fileLinks: string[] = [];
    //     for (const file of files) {
    //         fileLinks.push(URL.createObjectURL(file.file));
    //     }
    //     this.setState({ fileLinks });
    // };

    revokeLinks = (): void => {
        if (this.state.fileLinks) {
            for (const fileLink of this.state.fileLinks) {
                URL.revokeObjectURL(fileLink);
            }
        }
    };

    handleNewFiles = (files: File[]): void => {
        if (!this.props.multiple) {
            files = [files[0]];
        }

        this.props.onNewFiles(files);
    };

    handleRemove = (id: string, e: React.MouseEvent): void => {
        this.props.onRemove(id, e);
    };

    handleRemoveButtonClick = (e: React.MouseEvent): void => {
        const fileId = this.props.files?.[0]?.id;
        this.props.onRemove(fileId, e);
    };

    handleMoreItemsClick = (): void => {
        this.setState({ isMenuOpen: !this.state.isMenuOpen });
    };

    renderFiles = (): React.ReactElement => {
        if (this.props.multiple) {
            return <Tokenizer tokens={this.tokens}
                              maxWidth={this.maxTokenWidth}
                              maxVisibleTokens={1}
                              onClick={this.props.onClick}
                              onMoreItemsClick={this.handleMoreItemsClick}
                              showMoreTokensTooltip={this.props.isReadOnly}/>;

        }
        const file = this.props.files?.[0];
        const hasFile = isDefined(file);
        const hasOnClick = isDefined(this.props.onClick);
        const link = hasOnClick && hasFile ? null : `${FILES_API_URL}/${file?.id}`;
        return <FileNamesWrapper $isReadOnly={this.props.isReadOnly}
                                 $hasError={this.props.hasError}
                                 data-testid={TestIds.FileInputFileNames}>
            {hasFile &&
                <Clickable onClick={this.props.onClick?.bind(null, file.id)} link={link}
                           useEllipsis>
                    {file.name}
                </Clickable>}
        </FileNamesWrapper>;
    };

    renderIcon = (): React.ReactElement => {
        if (this.props.isReadOnly) {
            return null;
        }
        return (
            <IconWrapper isDisabled={this.props.isDisabled}>
                {<IconDelimiter/>}
                {(this.props.multiple || !this.hasFiles) && <FileUploadButton onFileChange={this.handleNewFiles}
                                                                              isDecorative
                                                                              preventHover
                                                                              multiple={this.props.multiple}
                                                                              width={IconSize.M}
                                                                              name={this.props.name}
                                                                              hotspotId={"FileInputUploadButton"}/>}
                {!this.props.multiple && this.hasFiles &&
                    <IconButton title={this.props.t("Components:FileInput.RemoveFile")}
                                hotspotId={"FileInputRemoveButton"}
                                onClick={this.handleRemoveButtonClick}
                                isDecorative>
                        <CloseIcon width={IconSize.M} preventHover/>
                    </IconButton>
                }
            </IconWrapper>
        );
    };

    renderTokens = (): React.ReactElement => {
        return <>
            {this.tokens.slice(1, this.tokens.length).map((tokenDef: ITokenProps, index: number) => {
                return (
                    <Token key={index}
                           onClick={this.props.onClick}
                           {...tokenDef}/>
                );
            })}
        </>;
    };

    renderMenu = (): React.ReactElement => {
        if (!this.state.isMenuOpen) {
            return null;
        }

        return (
            <FloatingPopper
                referenceElement={this._refOpener?.current}
                placement={"bottom-start"}
                useZoomCorrection={false}
                modifiers={[
                    offset({ crossAxis: POPOVER_INPUT_SHARP_LEFT_OFFSET_X * getZoomLevel() }),
                    shift({ padding: 19, rootBoundary: "viewport" }),
                ]}
            >
                {(popperProps) => (
                    <FileInputStyledMenu
                        tabIndex={0}
                        ref={popperProps.ref}
                        style={popperProps.style}
                        data-popper-placement={popperProps.placement}
                        data-testid={TestIds.SelectMenu}>
                        {this.renderTokens()}
                    </FileInputStyledMenu>
                )}
            </FloatingPopper>
        );
    };

    handleInputRef = (ref: HTMLInputElement) => {
        handleRefHandlers(ref, this._refOpener, this.props.passRef);
    };

    render() {
        if (this.props.isReadOnly) {
            return this.renderFiles();
        }


        return (<>
                <StyledFileInput
                    ref={this.handleInputRef}
                    isDisabled={this.props.isDisabled}
                    isSharpLeft={this.props.isSharpLeft}
                    isSharpRight={this.props.isSharpRight}
                    $isReadOnly={this.props.isReadOnly}
                             $hasValue={this.hasFiles}
                    _width={this.props.width}
                    _height={"34px"}>
                    <ErrorWrapper>{this.props.errorAndTooltip}</ErrorWrapper>
                    <FileDropArea isLocalDropArea={this.props.isLocalDropArea}
                              isReadOnly={this.props.isReadOnly}
                                  onNewFiles={this.handleNewFiles}
                                  showContent>
                        {this.renderFiles()}
                        {this.renderIcon()}
                    </FileDropArea>
                </StyledFileInput>
                {this.renderMenu()}
            </>
        );
    }
}

export default withTranslation(["Components"])(withErrorAndTooltip(FileInput, { preventValueTooltip: true }));

export type { IProps as IFileInputProps };